import { useEffect, useMemo, useState } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { extractErrorMessage } from '@/pages/painel-pedidos/lib/errors';
import { formatBRL } from '@/pages/painel/lib/radarConstants';

interface EmpresaRadar {
  id: number;
  nome: string;
  cidade: string | null;
  uf: string | null;
  segmento: string | null;
  valor_potencial: number | null;
  status: string | null;
  created_at: string;
  radar_count: number;
}

export default function RadarEmpresas() {
  const { password } = useOutletContext<{ password: string }>();
  const [empresas, setEmpresas] = useState<EmpresaRadar[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: invokeErr } = await supabase.functions.invoke('radar-admin', {
        body: { action: 'listar_empresas', password },
      });
      if (invokeErr) throw new Error(await extractErrorMessage(invokeErr));
      const parsed = data as { empresas?: EmpresaRadar[]; error?: string };
      if (parsed?.error) throw new Error(parsed.error);
      setEmpresas(parsed?.empresas ?? []);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erro ao carregar empresas.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [password]);

  const filtradas = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return empresas;
    return empresas.filter((e) =>
      [e.nome, e.cidade, e.segmento].filter(Boolean).join(' ').toLowerCase().includes(q),
    );
  }, [empresas, search]);

  const vinculadas = empresas.filter((e) => e.radar_count > 0).length;

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-[11px] text-gray-500 uppercase tracking-wider font-semibold">Empresas</p>
          <p className="text-2xl font-bold text-gray-900">{empresas.length}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-[11px] text-gray-500 uppercase tracking-wider font-semibold">Vinculadas ao Radar</p>
          <p className="text-2xl font-bold text-[#E8610A]">{vinculadas}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-[11px] text-gray-500 uppercase tracking-wider font-semibold">Clientes</p>
          <p className="text-2xl font-bold text-green-600">{empresas.filter((e) => e.status === 'cliente').length}</p>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-gray-200 p-4">
        <div className="relative max-w-md">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por nome, cidade ou segmento..."
            className="w-full border border-gray-200 rounded-lg pl-9 pr-4 py-2 text-sm text-gray-800 focus:outline-none focus:border-[#E8610A] focus:ring-2 focus:ring-[#E8610A]/20 transition-all bg-white"
          />
        </div>
      </div>

      {error && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-3">{error}</div>}

      {loading && empresas.length === 0 ? (
        <div className="flex justify-center py-16">
          <div className="w-8 h-8 border-2 border-[#E8610A] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : filtradas.length === 0 ? (
        <div className="text-center py-14 bg-white rounded-2xl border border-gray-200">
          <div className="w-14 h-14 flex items-center justify-center bg-gray-100 rounded-full mx-auto mb-3">
            <i className="ri-store-2-line text-xl text-gray-400"></i>
          </div>
          <p className="text-sm text-gray-500">Nenhuma empresa encontrada.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtradas.map((e) => (
            <Link
              key={e.id}
              to={`/painel/clientes/${e.id}`}
              className="group bg-white rounded-2xl border border-gray-200 p-5 hover:border-[#E8610A]/40 transition-colors cursor-pointer"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-10 h-10 flex items-center justify-center bg-[#E8610A]/10 text-[#E8610A] rounded-xl flex-shrink-0">
                    <i className="ri-building-4-line text-lg"></i>
                  </div>
                  <div className="min-w-0">
                    <h3 className="font-semibold text-gray-900 truncate group-hover:text-[#E8610A] transition-colors">
                      {e.nome}
                    </h3>
                    {e.segmento && <p className="text-xs text-gray-500 truncate">{e.segmento}</p>}
                  </div>
                </div>
              </div>

              <div className="mt-3 flex items-center gap-2 flex-wrap">
                {(e.cidade || e.uf) && (
                  <span className="inline-flex items-center gap-1 text-xs text-gray-500">
                    <i className="ri-map-pin-line"></i>
                    {[e.cidade, e.uf].filter(Boolean).join(' - ')}
                  </span>
                )}
                {e.radar_count > 0 && (
                  <span className="text-[11px] font-semibold text-[#E8610A] bg-[#E8610A]/10 rounded-full px-2 py-0.5 whitespace-nowrap">
                    <i className="ri-radar-line mr-1"></i>
                    {e.radar_count} no radar
                  </span>
                )}
                {e.status === 'cliente' && (
                  <span className="text-[11px] font-semibold text-green-700 bg-green-50 border border-green-200 rounded-full px-2 py-0.5">
                    Cliente
                  </span>
                )}
              </div>

              <div className="mt-4 pt-4 border-t border-gray-100 text-right">
                <p className="text-[10px] text-gray-400 uppercase tracking-wider font-semibold">Potencial</p>
                <p className="text-sm font-bold text-gray-900">{formatBRL(e.valor_potencial)}</p>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}